import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { serviceProviderService, SpLandingDto } from '../../services/serviceProvider.service';
import './ServiceProvider.scss';
import { CompactHeader } from '../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../components/RegistrationVisualCard/RegistrationVisualCard';
import { DiscontinueRegistrationModal } from '../../components/DiscontinueRegistrationModal/DiscontinueRegistrationModal';

/**
 * Pixel-faithful port of `service-provider/register_sp.blade.php`.
 *
 * Entry URL: /account/investor/step?status=<Crypt::encrypt(email)>
 * Continue button → /service-provider/user-consent?uniqueCode=<status>
 * (Laravel: `route('services_provider_user', ['unique_codes' => $status])`.)
 */
const SpLanding: React.FC = () => {
  const [params] = useSearchParams();
  const status = params.get('status') ?? '';
  const navigate = useNavigate();

  const [data, setData] = useState<SpLandingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!status) {
      setError('Missing or invalid invitation link.');
      return;
    }
    serviceProviderService
      .landing(status)
      .then(setData)
      .catch(() => setError('Invalid or expired invitation link.'));
  }, [status]);

  const onContinue = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!consentChecked || submitting) return;
    setSubmitting(true);
    setTimeout(() => {
      navigate(`/service-provider/user-consent?uniqueCode=${encodeURIComponent(status)}`);
    }, 300);
  };

  // Discontinue (new — register_sp.blade.php only had Continue). Soft abort: the API records
  // the decision and emails a confirmation; the invite link keeps working afterwards.
  const [showDiscontinue, setShowDiscontinue] = useState(false);
  const [discontinueReason, setDiscontinueReason] = useState('');
  const [discontinuing, setDiscontinuing] = useState(false);
  const [discontinueError, setDiscontinueError] = useState<string | null>(null);
  const [discontinuedResult, setDiscontinuedResult] = useState<{ message: string; emailSent: boolean } | null>(null);

  const onDiscontinue = async () => {
    if (!status) return;
    setDiscontinuing(true);
    setDiscontinueError(null);
    try {
      const res = await serviceProviderService.discontinue(status, discontinueReason);
      setDiscontinuedResult({ message: res.message, emailSent: res.emailSent });
      setShowDiscontinue(false);
    } catch (err: any) {
      setDiscontinueError(err?.response?.data?.message
        || 'Could not record your decision. Please try again.');
    } finally {
      setDiscontinuing(false);
    }
  };

  const greetingName = data?.fullName || 'Partner';
  const continueDisabled = !consentChecked || submitting || discontinuing;
  // Discontinue needs a valid, loaded invite — never offered on a broken link.
  const canDiscontinue = !!data && !error && !submitting;

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Stepper Progress */}
              <RegistrationStepper currentStep={1} title="Service Provider Registration" maxWidth="550px" />

              {/* Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '550px', margin: '0 auto' }}>
                <div className="login-form-style3-main_full">
                  <div className="login-register3-form-middle" style={{ textAlign: 'left' }}>
                    {error && (
                      <div style={{ color: '#be1717', marginBottom: 12 }}>{error}</div>
                    )}

                    <h4 className="mt-0">Dear {greetingName},</h4>
                    {discontinuedResult ? (
                      <>
                        <p><strong>Registration discontinued.</strong></p>
                        <p>{discontinuedResult.message}</p>
                        {!discontinuedResult.emailSent && (
                          <p style={{ fontSize: 12, color: '#64748b' }}>
                            If you need a written confirmation, please contact{' '}
                            <a href="mailto:support@facilonservices.com">support@facilonservices.com</a>.
                          </p>
                        )}
                        <p style={{ fontSize: 12, color: '#64748b' }}>
                          Changed your mind? You can reopen your invitation link at any time to continue.
                        </p>
                      </>
                    ) : (
                    <>
                    {data?.discontinued && (
                      <p style={{ fontSize: 12, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '8px 10px' }}>
                        You chose to discontinue this registration
                        {data.discontinuedOn ? ` on ${data.discontinuedOn}` : ''}.
                        You can still continue below if you have changed your mind.
                      </p>
                    )}
                    <p>
                      Welcome to <strong>Facilon Service Provider User Registration</strong>.
                    </p>
                    <p>
                      You have been invited to create your user account to access
                      your organisation&rsquo;s workspace on the Facilon platform. Before
                      proceeding, please review and provide your consent to the applicable
                      Privacy Notice, Terms of Use, and related policies governing platform
                      access and the processing of your information.
                    </p>
                    <p>
                      By continuing, you confirm that you are authorised to register as a
                      user for your organisation and agree to the stated terms.
                    </p>

                    <div className="highlight">
                      <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          id="consentCheckbox"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          className="w-4 h-4 text-[#be1717] bg-white border-slate-300 rounded focus:ring-[#be1717] cursor-pointer align-middle"
                          style={{ marginRight: '0.5rem', marginTop: '0.2rem', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>
                          I/We confirm that I/We am/are duly authorized to register and bind
                          my/our organization and agree to proceed with registration.
                        </span>
                      </label>
                    </div>

                    {discontinueError && (
                      <div style={{ color: '#be1717', fontSize: 13, marginTop: 10 }}>{discontinueError}</div>
                    )}

                    <div className="single-field mb-0 text-center border-t pt-3 mt-3" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Link
                        to="#"
                        id="continueBtn"
                        className={`button-1${continueDisabled ? ' disabled' : ''}`}
                        onClick={onContinue}
                      >
                        {submitting ? 'Submitting...' : 'Continue'}
                      </Link>
                      {canDiscontinue && (
                        <button
                          type="button"
                          id="discontinueBtn"
                          className="button-2"
                          onClick={() => { setDiscontinueError(null); setDiscontinueReason(''); setShowDiscontinue(true); }}
                          disabled={discontinuing}
                          title="Do not proceed with this registration"
                        >
                          Discontinue
                        </button>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="interest" />
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />

      <DiscontinueRegistrationModal
        show={showDiscontinue}
        busy={discontinuing}
        subject="your Service Provider user registration"
        email={data?.email}
        reason={discontinueReason}
        onReasonChange={setDiscontinueReason}
        onCancel={() => setShowDiscontinue(false)}
        onConfirm={onDiscontinue}
      />
    </>
  );
};

export default SpLanding;
